<template>
  <div class="anamnesis-container">
    <Breadcrumbs />

    <AssetFilters title="Entités numériques" asset-label="Entités numériques" :active-filter="viewFilter" :stats="stats"
      :is-loading="isLoading" @update:active-filter="setFilter" @refresh="refreshAssets" />

    <DigitalEntitySubset :entities="filteredAssets" :show-toolbar="true" :show-actions="true" :show-edit-button="true"
      :show-delete-button="true" @create="openCreationModal" @edit="editObject" @delete="handleDeleteObject"
      @view="showObject" />

    <!-- Composant DigitalEntityCreator -->
    <DigitalEntityCreator v-model:show="showCreatorModal" :user-id="userId" :mode="creatorMode"
      :initial-data="selectedEntity" @digital-entity-created="handleEntityCreated"
      @digital-entity-updated="handleEntityUpdated" />

    <Notification ref="notification" />
  </div>
</template>

<script setup lang="ts">
import { useDigitalEntities } from '@/composables/useAssets';
import { useAuthStore } from '@/stores/auth';
import type { NotificationType } from "@/types/notification";
import { computed, ref } from "vue";
import { useRouter } from 'vue-router';
import type { DigitalEntity } from "../../api/digitalEntities";
import Breadcrumbs from "../../components/Breadcrumbs.vue";
import Notification from "../../components/Notification.vue";
import AssetFilters from "../../components/UI/AssetFilters.vue";
import DigitalEntityCreator from "../../components/creators/DigitalEntityCreator.vue";
import DigitalEntitySubset from "../../components/subsets/DigitalEntitySubset.vue";
import { useBreadcrumbsStore } from "../../stores/breadcrumbs";

// Stores et composables
const router = useRouter();
const authStore = useAuthStore();
const breadcrumbsStore = useBreadcrumbsStore();
const {
  filteredAssets,
  stats,
  isLoading,
  viewFilter,
  setFilter,
  refreshAssets,
  addAsset: addDigitalEntity,
  updateAsset: updateDigitalEntity
} = useDigitalEntities({ autoLoad: true });

// État local
const notification = ref<InstanceType<typeof Notification> | null>(null);
const showCreatorModal = ref(false);
const creatorMode = ref<'create' | 'edit'>('create');
const selectedEntity = ref<DigitalEntity | undefined>(undefined);
const isDeleting = ref(false);

// Computed
const userId = computed((): string => authStore.user?.uuid || '');

const crumbs = [
  { name: 'Outils', path: '/dashboard' },
  { name: 'Entités numériques', path: '/mydigitalentities' }
];

breadcrumbsStore.updateBreadcrumbs(crumbs);

const showObject = (entity: DigitalEntity): void => {
  router.push(`/objects/${entity.uuid}`);
};

const editObject = (entity: DigitalEntity): void => {
  // Vérifier si l'utilisateur peut éditer cette entité (pas d'édition sur entités publiques d'autres utilisateurs)
  if (!authStore.canEditAsset(entity as unknown as { user_id: string; is_public: boolean })) {
    showNotification("Vous ne pouvez pas éditer cette entité numérique", "error");
    return;
  }

  selectedEntity.value = entity;
  creatorMode.value = 'edit';
  showCreatorModal.value = true;
};

const handleDeleteObject = async (entity: DigitalEntity): Promise<void> => {
  console.log('[MyDigitalEntities] handleDeleteObject appelé pour:', entity.uuid);
  
  // Protection contre les doubles clics
  if (isDeleting.value) {
    console.log('[MyDigitalEntities] Déjà en cours de suppression, ignoré');
    return;
  }
  
  // Vérifier si l'utilisateur peut supprimer cette entité (pas de suppression sur entités publiques d'autres utilisateurs)
  if (!authStore.canDeleteAsset(entity as unknown as { user_id: string; is_public: boolean })) {
    showNotification("Vous ne pouvez pas supprimer cette entité numérique", "error");
    return;
  }

  isDeleting.value = true;
  console.log('[MyDigitalEntities] Début de la suppression API pour:', entity.uuid);
  
  try {
    const { digitalEntities } = await import("../../api/digitalEntities");
    console.log('[MyDigitalEntities] Appel API deleteDigitalEntity pour:', entity.uuid);
    await digitalEntities.deleteDigitalEntity(entity.uuid);
    console.log('[MyDigitalEntities] Suppression API réussie, rafraîchissement...');
    // Rafraîchir la liste depuis le serveur au lieu de juste supprimer localement
    await refreshAssets();
    console.log('[MyDigitalEntities] Rafraîchissement terminé');
    showNotification("Entité numérique supprimée avec succès", "success");
  } catch (error) {
    console.error("Erreur lors de la suppression de l'entité numérique:", error);
    showNotification("Erreur lors de la suppression de l'entité numérique", "error");
  } finally {
    isDeleting.value = false;
    console.log('[MyDigitalEntities] Fin de handleDeleteObject');
  }
};

const openCreationModal = (): void => {
  selectedEntity.value = undefined;
  creatorMode.value = 'create';
  showCreatorModal.value = true;
};

const handleEntityCreated = (entity: DigitalEntity): void => {
  console.log('[MyDigitalEntities] handleEntityCreated appelé avec:', entity);
  addDigitalEntity(entity);
  console.log('[MyDigitalEntities] addDigitalEntity appelé');
  showNotification("Entité numérique créée avec succès", "success");
};

const handleEntityUpdated = (entity: DigitalEntity): void => {
  updateDigitalEntity(entity);
  showNotification("Entité numérique mise à jour avec succès", "success");
};

const showNotification = (message: string, type: NotificationType): void => {
  if (notification.value) {
    notification.value.showNotification(message, type);
  }
};
</script>

<style>
/* Les styles sont gérés par les composants enfants */
</style>